import React, { useCallback, useRef, useState } from 'react';
import { Button, message, List, Typography, Badge } from 'antd';
import { CheckOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import DocScanner from '../components/DocScanner';

interface PageInfo {
  page_number: number;
  filename: string;
  file_size: number;
}

interface PendingPage {
  tmpId: string;
  status: 'uploading' | 'done' | 'error';
  page_number?: number;
}

const { Title, Text } = Typography;

const DocumentScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const scannerRef = useRef<any>(null);

  const [documentId, setDocumentId] = useState<number | null>(null);
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [pending, setPending] = useState<PendingPage[]>([]);
  const [finishing, setFinishing] = useState(false);

  const pendingCount = pending.filter(p => p.status === 'uploading').length;

  // Общий обработчик скана — мгновенно, без блокировки UI
  const handleCapture = useCallback((blob: Blob) => {
    const file = new File([blob], `scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
    const formData = new FormData();
    formData.append('file', file);
    const tmpId = `tmp_${Date.now()}`;

    setPending(prev => [...prev, { tmpId, status: 'uploading' }]);

    const upload = documentId
      ? api.post(`/documents/${documentId}/pages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

    upload
      .then(r => {
        const doc = r.data;
        if (!documentId) {
          setDocumentId(doc.id);
        }
        setPages(doc.pages.map((p: any) => ({ page_number: p.page_number, filename: p.filename, file_size: p.file_size })));
        setPending(prev => prev.map(p => p.tmpId === tmpId ? { ...p, status: 'done', page_number: doc.pages.length } : p));
        // Убираем done через 3 сек
        setTimeout(() => {
          setPending(prev => prev.filter(p => p.tmpId !== tmpId));
        }, 3000);
      })
      .catch(() => {
        setPending(prev => prev.map(p => p.tmpId === tmpId ? { ...p, status: 'error' } : p));
        message.error('Ошибка загрузки страницы');
      });
  }, [documentId]);

  // Закончить сканирование → AI анализ
  const handleFinish = useCallback(() => {
    if (!documentId) return;
    if (pendingCount > 0) {
      message.warning('Дождитесь завершения загрузки всех страниц');
      return;
    }
    setFinishing(true);
    api.post(`/documents/${documentId}/finish`)
      .then((res) => {
        if (res.data?.warning) {
          message.warning(res.data.warning);
        } else {
          message.success('Документ обработан');
        }
        navigate(`/documents/${documentId}`);
      })
      .catch((err) => {
        message.error('Ошибка обработки: ' + (err.response?.data?.detail || err.message));
      })
      .finally(() => setFinishing(false));
  }, [documentId, pendingCount, navigate]);

  const handleDeletePage = useCallback((pageNumber: number) => {
    message.info('Удаление страниц будет в следующей версии');
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {documentId ? `Документ №${documentId} — добавьте страницы` : 'Сканер документов'}
        </Title>
        {documentId && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={finishing}
            disabled={pendingCount > 0}
            onClick={handleFinish}
          >
            Закончить сканирование
            {pendingCount > 0 && <Badge count={pendingCount} style={{ marginLeft: 8 }} />}
          </Button>
        )}
      </div>

      <DocScanner ref={scannerRef} onCapture={handleCapture} />

      {pendingCount > 0 && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Text type="secondary"><LoadingOutlined /> Загрузка {pendingCount} страниц…</Text>
        </div>
      )}

      {pages.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 24 }}>Страницы документа ({pages.length})</Title>
          <List
            dataSource={pages}
            renderItem={page => (
              <List.Item
                actions={[
                  <Button icon={<DeleteOutlined />} danger size="small" onClick={() => handleDeletePage(page.page_number)}>
                    Удалить
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={`Страница ${page.page_number}`}
                  description={`${page.filename} · ${((page.file_size || 0) / 1024).toFixed(1)} КБ`}
                />
              </List.Item>
            )}
          />
        </>
      )}

      {documentId && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">
            Нажимайте «Сделать скан» для каждой страницы. Когда всё отсканировано — «Закончить сканирование».
          </Text>
        </div>
      )}
    </div>
  );
};

export default DocumentScannerPage;
